import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { makeRequest } from '../../../apis/makeRequest'
import { saveTransportPlate } from '../../../apis/backendRequests'
import { notificationActions } from '../../../actions'
import {
    getApiErrorCode,
    getApiErrorMessage,
    normalizePlate,
    plateNumbers,
    MAX_PLATE_LENGTH,
    PLATE_MAX_AXLES,
    PLATE_MAX_DECIMAL,
    transportErrors,
    transportMaxLengths,
} from '../../../helpers/logisticaHelper'
import { StyledTextField, DarkButton } from '../../../ui/StyledComponents'

const emptyForm = {
    plate: '',
    brandModel: '',
    tareTons: '',
    volumeM3: '',
    axles: '',
    driverDni: '',
    driverName: '',
    notes: '',
}

const toForm = (plate) => Object.fromEntries(
    Object.keys(emptyForm).map((key) => [key, plate?.[key] != null ? String(plate[key]) : ''])
)

const parseNumber = (value) => Number(value.trim().replace(',', '.'))

const decimalError = (value) => {
    if (!value.trim()) return ''
    const n = parseNumber(value)
    if (Number.isNaN(n)) return 'Tiene que ser un número'
    if (n < 0 || n > PLATE_MAX_DECIMAL) return 'Entre 0 y 9.999.999.999'
    return ''
}

const axlesError = (value) => {
    if (!value.trim()) return ''
    const n = parseNumber(value)
    if (!Number.isInteger(n)) return 'Tiene que ser un número entero'
    if (n < 0 || n > PLATE_MAX_AXLES) return 'Entre 0 y 32.767'
    return ''
}

const textOrNull = (value) => value.trim() || null
const numberOrNull = (value) => (value.trim() ? parseNumber(value) : null)

// Alta y edición de una patente del transporte. El PUT reemplaza todos los campos: se manda siempre el formulario
// completo. `plate` es la patente a editar (null = alta) e `initialPlate` precarga el número en un alta.
// `onSaved` recibe el TransportDTO completo que devuelve la API
export default function PatenteDialog({ open, transport, plate, initialPlate = '', onClose, onSaved, onTransportNotFound }) {
    const dispatch = useDispatch()
    const [form, setForm] = useState(emptyForm)
    const [plateApiError, setPlateApiError] = useState('')
    const [saving, setSaving] = useState(false)

    const isNew = !plate

    useEffect(() => {
        if (!open) return
        setForm(isNew ? { ...emptyForm, plate: initialPlate } : toForm(plate))
        setPlateApiError('')
        setSaving(false)
    }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

    const setField = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }))
        if (field === 'plate') setPlateApiError('')
    }

    const normalizedPlate = normalizePlate(form.plate)
    // En el alta, una patente que ya tiene el transporte se pisaría sin aviso: se edita desde su chip
    const plateExists = isNew && plateNumbers(transport).includes(normalizedPlate)

    const errors = {
        plate: plateApiError
            || (form.plate && !normalizedPlate ? 'Tiene que tener letras o números' : '')
            || (normalizedPlate.length > MAX_PLATE_LENGTH ? `Hasta ${MAX_PLATE_LENGTH} letras y números` : '')
            || (plateExists ? 'La patente ya está cargada en el transporte: editala desde su chip' : ''),
        tareTons: decimalError(form.tareTons),
        volumeM3: decimalError(form.volumeM3),
        axles: axlesError(form.axles),
    }
    const isValid = !!normalizedPlate && Object.values(errors).every((e) => !e)

    const handleGuardar = () => {
        if (!isValid || saving) return
        setSaving(true)
        const payload = {
            plate: normalizedPlate,
            brandModel: textOrNull(form.brandModel),
            tareTons: numberOrNull(form.tareTons),
            volumeM3: numberOrNull(form.volumeM3),
            axles: numberOrNull(form.axles),
            driverDni: textOrNull(form.driverDni),
            driverName: textOrNull(form.driverName),
            notes: textOrNull(form.notes),
        }
        makeRequest(
            () => saveTransportPlate(transport.code, payload).catch((err) => {
                if (err.intercepted) setSaving(false)
                throw err
            }),
            (res) => {
                setSaving(false)
                dispatch(notificationActions.showSuccess({ message: `Patente ${normalizedPlate} guardada.` }))
                onSaved(res.data, normalizedPlate)
            },
            (err) => {
                setSaving(false)
                const code = getApiErrorCode(err)
                if (code === transportErrors.INVALID_PLATE) {
                    setPlateApiError(getApiErrorMessage(err))
                    return
                }
                dispatch(notificationActions.showError({ message: getApiErrorMessage(err) }))
                if (code === transportErrors.NOT_FOUND) onTransportNotFound?.()
            },
            { checkEmptyResults: false }
        )
    }

    const field = (name, label, props = {}) => (
        <StyledTextField
            size="small"
            label={label}
            value={form[name]}
            onChange={setField(name)}
            error={!!errors[name]}
            helperText={errors[name] || ' '}
            {...props}
        />
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isNew ? `Nueva patente de ${transport?.code ?? ''}` : `Patente ${plate.plate} de ${transport?.code ?? ''}`}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, pt: 1 }}>
                    {field('plate', 'Patente', {
                        required: true,
                        disabled: !isNew,
                        autoFocus: isNew,
                        inputProps: { maxLength: 20 },
                    })}
                    {field('brandModel', 'Marca / modelo', { inputProps: { maxLength: transportMaxLengths.brandModel } })}
                    {field('tareTons', 'Tara (t)', { inputProps: { inputMode: 'decimal' } })}
                    {field('volumeM3', 'Volumen (m³)', { inputProps: { inputMode: 'decimal' } })}
                    {field('axles', 'Ejes', { inputProps: { inputMode: 'numeric' } })}
                    {field('driverDni', 'DNI del chofer', { inputProps: { maxLength: transportMaxLengths.driverDni } })}
                    {field('driverName', 'Nombre del chofer', {
                        inputProps: { maxLength: transportMaxLengths.driverName },
                        sx: { gridColumn: { sm: '1 / -1' } },
                    })}
                    {field('notes', 'Notas', {
                        multiline: true,
                        minRows: 2,
                        inputProps: { maxLength: transportMaxLengths.notes },
                        sx: { gridColumn: { sm: '1 / -1' } },
                    })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                    * Solo la patente es obligatoria. El resto de los datos es opcional.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <DarkButton onClick={handleGuardar} disabled={!isValid || saving}>Guardar</DarkButton>
            </DialogActions>
        </Dialog>
    )
}
